// filepath: apps/web/src/i18n/locales.ts
//
// FR-I18N-3 — shared locale definitions.
//
// Single source of truth for the v1 locale set across the SPA. Before
// FR-I18N-3 this was duplicated as a literal `["ru", "kk", "en"]` array
// in `useTranslation.ts`, `localeMap.ts`, `i18nLocalePreference.ts`, and
// the i18n runtime's `supportedLngs`. The duplication was acceptable
// for 3 locales but becomes a maintenance hazard at 5 (and beyond).
//
// ARCHITECTURE_WEB.md §8 — locale policy:
//   Primary Russian. Secondary Kazakh. Fallback English.
//   Plus: Ukrainian (`uk`) + Belarusian (`be`) — CIS-region expansion
//   under FR-I18N-3 (GitLab issue #4 / ISS-f4fa1b7b-282).
//
// Fallback chain order: `ru → kk → en → uk → be`. The two new locales
// are appended AFTER English (the developer / fallback language); the
// primary hierarchy is unchanged. Adding a sixth locale later (e.g. `de`
// under a future FR-MOB-I18N-* or FR-UI-I18N-EU-*) is one array
// extension + matching bundle additions — no callers need to change
// because they all consume the constants exported from this module.
//
// SECURITY:
//   - All exports are pure data + a type guard. No runtime state.
//   - `isLocale` is a type guard used by both the i18n runtime's
//     `supportedLngs` filter and the `?locale=` debug override path.

/** All locale codes the v1 SPA ships. Order is the fallback chain order. */
export const LOCALES = ["ru", "kk", "en", "uk", "be"] as const;

/** Type-level alias for `LOCALES`. Mirrors ARCHITECTURE_WEB.md §8. */
export type Locale = (typeof LOCALES)[number];

/**
 * Fallback chain — locale codes walk this list when a key is missing
 * for the active locale. The first non-empty entry wins.
 *
 * Order rationale (ARCHITECTURE_WEB.md §8):
 *   1. `ru` — primary language for the project's CIS-region tenants.
 *   2. `kk` — secondary; co-official in Kazakhstan where the platform
 *      originated.
 *   3. `en` — fallback / developer / international locale.
 *   4. `uk` — Ukrainian; added under FR-I18N-3 (CIS-region expansion).
 *   5. `be` — Belarusian; added under FR-I18N-3.
 */
export const FALLBACK_ORDER: readonly Locale[] = LOCALES;

/** The locale that i18next falls back to when no preference is set. */
export const DEFAULT_LOCALE: Locale = "ru";

/** Type guard: is `value` one of the shipped locale codes? */
export function isLocale(value: unknown): value is Locale {
  return (
    typeof value === "string" && (LOCALES as readonly string[]).includes(value)
  );
}

/**
 * Map a possibly region-tagged language string (`"ru-RU"`, `"kk-KZ"`,
 * `"uk-UA"`, `"be-BY"`, etc.) to a shipped locale. Returns
 * `DEFAULT_LOCALE` if no match is found.
 *
 * Used by `useTranslation()` to coerce i18next's `i18n.language` (which
 * may carry a region suffix from a navigator-detected value) to a
 * v1-shipped locale code.
 */
export function coerceToLocale(lang: string | undefined | null): Locale {
  if (!lang) return DEFAULT_LOCALE;
  if (isLocale(lang)) return lang;
  const base = lang.split("-")[0];
  if (isLocale(base)) return base;
  return DEFAULT_LOCALE;
}
